import axios from 'axios';
import { ref, computed, watch } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useCookies } from '@/utils/cookies';
import mixpanel from '@/mixpanel.ts';
import type { WidgetObject } from '@/types/Widget';
import type { UserData } from '@/types/User';
import { logOutApi, refreshToken, refreshUserData } from '@/utils/api';
import { configureEcho } from '@laravel/echo-vue';
import { useToastStore } from '@/stores/toast';

export const useUserStore = defineStore('user', () => {
  const cookies = useCookies();

  const accessToken = ref('');
  const toast = useToastStore();
  const userData = ref<UserData>();
  const demoEvent = ref();
  const demoTemplate = ref();

  type logInData = {
    token: string;
    user: UserData;
    expires_at: string;
    demo?: {
      event: {
        CampaignsEvent: {
          id: number;
          uuid: string;
          name: string;
        };
      };
      template?: {
        description?: string;
        global?: boolean;
        id?: number;
        name?: string;
        widgets?: WidgetObject[];
      };
    };
  };

  function logIn(response: logInData, short: boolean, mixpanelTrackLogin: boolean = true) {
    accessToken.value = response.token;
    userData.value = response.user;
    mixpanel.identify(userData.value.email);
    mixpanel.people.set({
      $email: userData.value.email,
      $name: userData.value.name,
      $role: userData.value.role_name,
      $account: userData.value.account_name,
      $account_id: userData.value.account_id,
    });
    if (mixpanelTrackLogin) {
      mixpanel.track('Login');
    }
    const expiry = new Date(response.expires_at);
    cookies
      .set('expiry', response.expires_at, expiry)
      .set('token', response.token, expiry)
      .set('user', userData.value, expiry)
      .set('short', short, expiry);

    if (response.demo) {
      localStorage.setItem('demoEvent', JSON.stringify(response.demo.event));
      localStorage.setItem('demoTemplate', JSON.stringify(response.demo.template));
      demoEvent.value = response.demo.event;
      demoTemplate.value = response.demo.template;
    }
  }

  function restoreFromCookies() {
    if (cookies?.get('user')) {
      accessToken.value = cookies.get('token');
      userData.value = cookies.get('user');

      if (localStorage.getItem('demoEvent')) {
        demoEvent.value = JSON.parse(`${localStorage.getItem('demoEvent')}`);
      }
      if (localStorage.getItem('demoTemplate')) {
        demoTemplate.value = JSON.parse(`${localStorage.getItem('demoTemplate')}`);
      }
      if (userData.value) {
        mixpanel.identify(userData.value.email);
      }
    }
  }

  function getInitials() {
    if (userData.value && userData.value) {
      const { name } = userData.value;
      const namesSplit = name.split(' ');
      const init1 = Array.from(namesSplit[0])[0];
      let init2 = '';

      if (namesSplit[1]) {
        init2 = Array.from(namesSplit[1])[0];
      }

      return `${init1}${init2}`;
    }
    return '';
  }

  function getFirstName() {
    if (userData.value && userData.value) {
      const { name } = userData.value;
      const namesSplit = name.split(' ');
      return `${namesSplit[0]}`;
    }
    return '';
  }

  async function logOut() {
    mixpanel.track('Logout');
    delete userData.value;

    await logOutApi(accessToken.value).catch(() => {});

    accessToken.value = '';
    cookies.remove('expiry');
    cookies.remove('token');
    cookies.remove('short');
    cookies.remove('user');

    // clear the stores
    localStorage.clear();
    location.replace(location.origin);
  }

  const expiredMessage = 'Your session has expired. Please log in again.';

  let lastTokenCheck = Date.now() - (10 * 60 * 1000);

  const checkTokenExpiry = async () => {
    const now = Date.now();
    if (lastTokenCheck + ( 10 * 60 * 1000) > now) {
      return true;
    }
    lastTokenCheck = now;
    const expiryDate = cookies?.get('expiry') ? Date.parse(cookies.get('expiry')) : 0;
    const short = cookies?.get('short') !== 'false';
    const expiryWindow = short
      ? now + 6 * 60 * 60 * 1000 // 6 hours in milliseconds
      : now + 7 * 86400 * 1000; // 1 week

    if (expiryDate <= expiryWindow) {
      if (cookies?.get('token')) {
        return await refreshToken(cookies.get('token'), short).then(
          (response) => {
            cookies.set('token', response.accessToken);
            cookies.set('expiry', response.expires_at);
            accessToken.value = response.accessToken;
            return true;
          },
          () => {
            toast.manual('error', expiredMessage);
            userData.value = undefined;
            return '/login';
          },
        );
      } else {
        toast.manual('error', expiredMessage);
        userData.value = undefined;
        return '/login';
      }
    } else {
      await requestUserDataRefresh();
    }
    return true;
  };

  async function requestUserDataRefresh() {
    const short = cookies.get('short') !== 'false';
    const data = await refreshUserData(accessToken.value, short).catch(() => {
      return null;
    });

    if (!data) {
      return;
    }

    if (data.accessToken) {
      logIn({
        token: data.accessToken,
        user: data.userData,
        expires_at: data.expires_at,
        demo: data.demo,
      }, short, false);
    } else if (data.userData) {
      userData.value = data.userData;
    }
  }

  if (import.meta.hot && import.meta.hot.data.userInterceptorId !== undefined) {
    axios.interceptors.request.eject(import.meta.hot.data.userInterceptorId);
  }
  const userInterceptorId = axios.interceptors.request.use(async config => {
    if (
      isLoggedIn.value &&
      !config.url?.includes('/auth/') &&
      !config.url?.includes('/saml/')
    ) {
      await checkTokenExpiry();
      config.headers.setAuthorization(`Bearer ${accessToken.value}`);
    }
    return config;
  });
  if (import.meta.hot) {
    import.meta.hot.data.userInterceptorId = userInterceptorId;
  }

  const isLoggedIn = computed(() => {
    return userData.value ? true : false;
  });

  const isOperationsUser = computed(() => {
    return userData.value?.role === 'operations';
  });

  const isBasicUser = computed(() => {
    // accepted: user
    return (
      userData.value?.role !== 'client_administrator' &&
      userData.value?.role !== 'staff' &&
      userData.value?.role !== 'admin' &&
      userData.value?.role !== 'super_admin'
    );
  });

  const isClientAdmin = computed(() => {
    return userData.value?.role === 'client_administrator';
  });

  const isNonStaff = computed(() => {
    // accepted: user, client_administrator
    return (
      userData.value?.role !== 'staff' &&
      userData.value?.role !== 'admin' &&
      userData.value?.role !== 'super_admin'
    );
  });

  const isStaff = computed(() => {
    return (
      userData.value?.role === 'staff' ||
      userData.value?.role === 'admin' ||
      userData.value?.role === 'super_admin'
    );
  });

  const isMeshhAdmin = computed(() => {
    return userData.value?.role === 'admin' || userData.value?.role === 'super_admin';
  });

  const hasScope = (scope: string) => {
    if (userData.value?.role === 'admin' || userData.value?.role === 'super_admin') {
      return true;
    }

    return userData?.value?.scopes?.includes(scope);
  };

  const hasFeature = (feature: string) => {
    // Check if user is admin/super_admin (with scorecard exception)
    if (userData.value?.role === 'admin' || userData.value?.role === 'super_admin') {
      return true;
    }

    return userData?.value?.features?.includes(feature) ?? false;
  };

  const hasAnyFeature = (features: string[]) => {
    return features.some((f) => hasFeature(f));
  };

  const hasAllFeatures = (features: string[]) => {
    return features.every((f) => hasFeature(f));
  };

  watch(accessToken, () => {
    if (accessToken.value && import.meta.env.VITE_REVERB_APP_KEY && import.meta.env.VITE_REVERB_HOST) {
      configureEcho({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        authEndpoint: import.meta.env.VITE_API_URL + '/broadcasting/auth',
        auth: {
          headers: {
            Authorization: `Bearer ${accessToken.value}`,
          },
        },
        forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
      });
    }
  });

  return {
    accessToken,
    checkTokenExpiry,
    requestUserDataRefresh,
    isLoggedIn,
    userData,
    demoEvent,
    demoTemplate,

    isBasicUser,
    isClientAdmin,
    isNonStaff,
    isStaff,
    isMeshhAdmin,
    isOperationsUser,
    hasScope,
    hasFeature,
    hasAnyFeature,
    hasAllFeatures,
    getInitials,
    getFirstName,
    logIn,
    restoreFromCookies,
    logOut,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}

